import { ICommandBarStyles, ILabelStyles } from "@fluentui/react";
import { debounce, forEach } from "lodash";
import { CommandBar, DetailsList, Selection, Icon, Panel, PanelType, SelectionMode, TooltipHost, MessageBar, MessageBarType, Spinner, IconButton, Stack, PrimaryButton, Label, DatePicker, Dropdown, IDropdownOption, TextField, TagPicker, ITag, IStackTokens, IBasePickerStyles } from "office-ui-fabric-react";
import { useCallback, useEffect, useState } from "react";
import { cancelBooking, getMyBookings, searchDeskBookings } from "../../../api/bookings";
import { loadLocations, getSites } from "../../../api/siteslocations";
import { searchUsers } from "../../../api/userInfo";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { IBooking, IBookingSearchParams, IMyBooking } from "../../../types/Booking.types";
import { ILocation, ISite } from "../../../types/SiteLocation.types";
import { IUserWithPhoto } from "../../../types/User.types";
import { CancelReasonDialog } from "./CancelReasonDialog";

export interface IManageBookingsPanelProps {
    onDismiss: () => void;
}

export const ManageBookingsPanel = (props: IManageBookingsPanelProps) => {
    const { onDismiss } = props;
    
    const [ waiting, setWaiting ] = useState(false);
    const [ cancelling, setCancelling ] = useState(false);
    const [ bookings, setBookings ] = useState<IMyBooking[]>();
    const [ bookingSelected, setBookingSelected ] = useState<boolean|undefined>();
    const [ selectedBookings, setSelectedBookings ] = useState<IMyBooking[]>([]);
    const today = new Date();
    const defaultFrom = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
    const defaultTo = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
    defaultTo.setDate(defaultTo.getDate() + 7);
    const [ fromDate, setFromDate ] = useState<Date>(defaultFrom);
    const [ toDate, setToDate ] = useState<Date>(defaultTo);
    const [ siteOptions, setSites ] = useState<IDropdownOption[]>([]);
    const [ chosenSite, setChosenSite ] = useState<number>();
    const [ locationOptions, setLocations ] = useState<IDropdownOption[]>([]);
    const [ chosenLocation, setChosenLocation ] = useState<number>();
    const [ chosenUser, setChosenUser ] = useState<number>();
    const [ showDialog, setShowDialog ] = useState(false);            

    const onSelectionChanged = () => {
        const selectedItems = selection.getSelection() as IMyBooking[];
        if (selectedItems !== undefined && selectedItems.length > 0) {
            setBookingSelected(true);
            setSelectedBookings(selectedItems);
        } else {
            setBookingSelected(false);
            setSelectedBookings([]);
        }
    };    
    
    const [ selection ] = useState(new Selection({ onSelectionChanged }));

    const search = async () => {
        let searchParams: IBookingSearchParams = {
            FromDate: fromDate.toISOString(),
            ToDate: toDate.toISOString(),
            SiteId: chosenSite,
            LocationId: chosenLocation,
            UserId: chosenUser  
        };
        const response = await searchDeskBookings(searchParams);
        if (response.ok) {
            const bookings = await response.json() as IMyBooking[];
            setBookings(bookings);
        }
    };
    
    const _onUserSearchChanged = async (filter: string, selectedItems?: ITag[]): Promise<ITag[]> => {
        return new Promise((resolve, reject) => {
            if (!Boolean(filter) || !Boolean(filter.trim())) resolve([]);
            searchUsers(filter).then((response) => {
                if (response instanceof Error) {
                    reject(response);
                } else {
                    resolve(response.map((c) => ({
                        key: c.Id,
                        name: c.FullName + " (" + c.Email + ")",
                        data: c
                    } as ITag)));
                }
            }).catch((err) => reject(err));
        });
    }  

    const _load = async () => {
        const siteResponse = await getSites();
        if (siteResponse.ok) {
            const sites = await siteResponse.json() as ISite[];
            const siteOptions:IDropdownOption[] = sites.map((s) => {
                return {
                    key: s.Id,
                    text: s.Title
                }
            });
            setSites(siteOptions);
        }
        const response = await searchDeskBookings({
            FromDate: fromDate.toISOString(),
            ToDate: toDate.toISOString()
        });
        if (response.ok) {
            const bookings = await response.json() as IMyBooking[];
            setBookings(bookings);
        }
    }

    const _cancelBulkBookings = async (bookings: IMyBooking[], cancelReason: string | undefined) => {
        setCancelling(true);
        let cancelSuccess = 0;
        for (let index = 0; index < bookings.length; index++) {
            const booking = bookings[index];
            booking.CancelledByAdmin = true;
            booking.CancellationReason = cancelReason;            
            const response = await cancelBooking(booking);
            if (response.ok) {
                cancelSuccess++;
            }
        }

        if (bookings.length == cancelSuccess) {
            alert('All selected bookings cancelled.');
            setCancelling(false);
            setSelectedBookings([]);
            _load();
        } else {        
            if (cancelSuccess == 0) {
                alert('Unable to cancel bookings');
                setCancelling(false);
                setSelectedBookings([]);
            } else {
                alert(cancelSuccess + ' out of ' + bookings.length + ' bookings cancelled. There were issues cancelling the remaining bookings')
                setCancelling(false);
                setSelectedBookings([]);
                _load();
            }
        }
    }

    const _setSite = async (siteId: number) => {
        setChosenSite(siteId);
        const locationResponse = await loadLocations(siteId);
        if (locationResponse.ok) {
            const locations = await locationResponse.json() as ILocation[];
            const locationOptions:IDropdownOption[] = locations.map((s) => {
                return {
                    key: s.Id,
                    text: s.Title
                }
            });
            setLocations(locationOptions);
        }
    }

    const _getTextFromItem = (item: ITag) => {
        return item.name;
    }

    useEffect(() => {
        _load();
    }, []);

    const numericalSpacingStackTokens: IStackTokens = {
      childrenGap: 10
    };

    const tagPickerStyle: Partial<IBasePickerStyles> = {
        root: { marginTop: 0 },
        input: { marginTop: 0 }
    }

    const cmdBarStyle: ICommandBarStyles = {
        root: {
            margin: "20px 0px"
        }
    }

    // Validate changes to dates
    useEffect(() => {
        if (fromDate && toDate && fromDate > toDate) {
            setToDate(new Date(fromDate));
        }
        if (fromDate && toDate && toDate < fromDate) {
            const newDate = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate(), Math.max(fromDate.getHours(), toDate.getHours()), Math.max(fromDate.getMinutes(), toDate.getMinutes()), 0);
            setToDate(newDate);
        }
    }, [fromDate, toDate]);
    
    const setBeginningDate = useCallback((date) => {
        const newDate = new Date(date);
        newDate.setHours(0); newDate.setMinutes(0); newDate.setSeconds(0);
        setFromDate(newDate);
    }, []);
    
    const setEndDate = useCallback((date) => {
        const newDate = new Date(date);
        newDate.setHours(23); newDate.setMinutes(59); newDate.setSeconds(59);
        setToDate(newDate);
    }, []);

    return (
        <Panel type={PanelType.custom} customWidth="900px" isOpen={true} headerText="Search Bookings" onDismiss={onDismiss}>
            <Stack tokens={numericalSpacingStackTokens}>
                <Stack horizontal tokens={numericalSpacingStackTokens}> 
                    <DatePicker label="From" value={fromDate} onSelectDate={(date) => { if (date) setBeginningDate(date)}} />
                    <DatePicker label="To" value={toDate} onSelectDate={(date) => { if (date) setEndDate(date)}} />
                    <Dropdown label="Site" placeholder="Select a site" options={siteOptions} onChange={(event, option, index) => { if (option) _setSite(option.key as number)}} />
                    <Dropdown label="Location" placeholder="Select a location" disabled={!chosenSite} options={locationOptions} onChange={(event, option, index) => { if (option) setChosenLocation(option.key as number)}} />
                    {/* <Dropdown label="Desk" placeholder="Select a desk" disabled={!chosenSite} options={deskOptions} onChange={(event, option, index) => { if (option) setChosenDesk(option.key as number)}} /> */}
                </Stack>
                <Label>User Search</Label>
                <TagPicker
                    onResolveSuggestions={_onUserSearchChanged} 
                    getTextFromItem={_getTextFromItem}
                    resolveDelay={1000}
                    pickerSuggestionsProps={{
                        noResultsFoundText: 'No userss Found',
                        suggestionsHeaderText: 'Suggested Users',
                    }}
                    inputProps={{
                        'aria-label': 'User Picker',
                    }}
                    itemLimit={1}
                    onChange={(items?: ITag[]) => {
                        if (items && items.length > 0) {
                            setChosenUser(items[0].key as number);
                        } else {
                            setChosenUser(undefined);
                        }
                    }}
                    styles={tagPickerStyle}
                />                
                <PrimaryButton iconProps={{iconName: "Search"}} onClick={search} />
            </Stack>
            { window.innerWidth < 640 && (
                <MessageBar messageBarType={MessageBarType.info}>You can scroll the table below with the mouse or your finger</MessageBar>
            )}
            <CommandBar
                items={[
                    // {
                    //     disabled: !bookingSelected,
                    //     key: "view",
                    //     text: window.innerWidth < 640 ? "View" : "View Booking",
                    //     onClick: () => {
                    //         const selectedItems = selection.getSelection() as IMyBooking[];
                    //         if (selectedItems) {
                    //             const selectedItem = selectedItems[0];
                    //             setViewBooking(selectedItem);
                    //         }
                    //     }
                    // },
                    {
                        disabled: !bookingSelected || cancelling,
                        key: "cancel",
                        text: window.innerWidth < 640 ? "Cancel" : "Cancel Booking",
                        onRender: cancelling ? () => <Spinner label="Cancelling..." /> : undefined,
                        onClick: () => {
                            const selectedItems = selection.getSelection() as IMyBooking[];
                            if (selectedItems) {
                                setSelectedBookings(selectedItems);
                                setShowDialog(true);
                            }
                        }
                    }
                ]}
                styles={cmdBarStyle}
            />
            { bookings !== undefined && bookings.length > 0 && [
                <DetailsList
                    items={bookings}
                    // className={styles.myBookingsTable}
                    columns={[
                        {
                            key: "deskName",
                            name: "Desk",
                            fieldName: "DeskName",
                            minWidth: 80,
                            maxWidth: 100,
                            onRender: (item: IMyBooking, index, column) => {
                                // For other orgs or if sites expand may want to list location and floorplan here too 
                                let deskName = item.DeskName;
                                if (!deskName || deskName.trim().length === 0) deskName = "Desk (Id: " + item.DeskId + ")";
                                return (
                                    <div>
                                        {deskName}
                                        <div style={{fontSize: 10}}>
                                            {item.SiteName}
                                        </div>
                                    </div>
                                )
                            }
                        },
                        {
                            key: "bookedBy",
                            name: "Booked By",
                            fieldName: "BookedByUserName",
                            minWidth: 80,
                            maxWidth: 100
                        },                        
                        {
                            key: "onBehalf",
                            name: "On Behalf Of",
                            fieldName: "OnBehalfOfEmail",
                            minWidth: 80,
                            maxWidth: 100,
                            onRender: (item: IMyBooking) => {
                                return item.OnBehalfOfEmail ? item.OnBehalfOfEmail : "Self";
                            },
                        },
                        {
                            key: "start",
                            name: "Start",
                            fieldName: "Start",
                            minWidth: 80,
                            maxWidth: 150,
                            onRender: (item: IMyBooking, index, column) => {
                                if (item.Start) {
                                    const date = new Date(item.Start);
                                    let dateStr = date.toLocaleDateString();
                                    if (window.innerWidth < 768) dateStr = dateStr.substr(0, 5);
                                    return  dateStr + " " + date.toLocaleTimeString().substr(0,5);
                                }
                            }
                        },
                        {
                            key: "finish",
                            name: "Finish",
                            fieldName: "Finish",
                            minWidth: 80,
                            maxWidth: 150,
                            onRender: (item: IMyBooking, index, column) => {
                                if (item.Finish) {
                                    const date = new Date(item.Finish);
                                    let dateStr = date.toLocaleDateString();
                                    if (window.innerWidth < 768) dateStr = dateStr.substr(0, 5);
                                    return  dateStr + " " + date.toLocaleTimeString().substr(0,5);
                                }
                            }
                        },
                        {
                            key: "parking",
                            name: "Parking",
                            fieldName: "IncludeParking",
                            iconName: "ParkingSolid",
                            isIconOnly: true,
                            minWidth: 40,
                            maxWidth: 40,
                            onRender: (item: IMyBooking, index, column) => {
                                if (item.IncludeParking) {
                                    return <TooltipHost content="Parking Booked">
                                        <Icon iconName="ParkingSolid" style={{fontSize: 24}} />
                                    </TooltipHost>;
                                }
                            }
                        },
                        {
                            key: "directions",
                            name: "Directions",
                            fieldName: "PostCode",
                            iconName: "MapDirections",
                            isIconOnly: true,
                            minWidth: 40,
                            maxWidth: 40,
                            onRender: (item: IMyBooking, index, column) => {
                                if (item.PostCode) {
                                    return <TooltipHost content="Get Directions">
                                        <IconButton iconProps={{iconName: "MapDirections"}} 
                                            style={{fontSize: 24}} 
                                            href={`https://www.google.com/maps/dir/?api=1&destination=${ item.PostCode }`}
                                            target="_blank"
                                        />
                                    </TooltipHost>
                                }
                            }
                        }
                    ]}
                    selection={selection}
                    selectionMode={SelectionMode.multiple}
                />
                // <Stack horizontal horizontalAlign="end" style={{marginTop: 20}}>
                //     <PrimaryButton text="Export" iconProps={{iconName: "Export"}}
                //         href={"/api/bookings/my/export"} target="_blank" />
                // </Stack>
            ]}
            { (bookings === undefined || bookings.length === 0) &&(
                <MessageBar messageBarType={MessageBarType.info}>No bookings found for the search options chosen above.</MessageBar>
            )}
            { showDialog && (
                <CancelReasonDialog 
                    onDismiss={(cancel, cancelReason) => {
                        if (cancel) {
                            _cancelBulkBookings(selectedBookings, cancelReason);
                            setShowDialog(false);
                        } else {
                            setShowDialog(false);
                        }
                    }}
                />
            )}            
        </Panel>    
    );
}
